import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Save, Loader2, Edit3, Clock, User, CheckCircle, Plus, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import moment from 'moment';

export const SessionNotesSection = ({
  expanded,
  onToggle,
  notes = [], // Array of note objects
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  saving = false,
  currentUserId = null, // Current user's ID to check ownership
  currentUserName = null, // Current user's name for new notes
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;
    await onAddNote(newNoteContent);
    setNewNoteContent('');
    setIsAddingNote(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleStartEdit = (note) => {
    setEditingNoteId(note.id);
    setEditingContent(note.content);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingContent('');
  };

  const handleSaveEdit = async (noteId) => {
    if (!editingContent.trim()) return;
    await onUpdateNote(noteId, editingContent);
    setEditingNoteId(null);
    setEditingContent('');
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm(t('sessionDetails.notes.deleteConfirm'))) {
      await onDeleteNote(noteId);
    }
  };

  const canEditNote = (note) => {
    return currentUserId && note.authorId === currentUserId;
  };

  return (
    <div className={`${colors.cardBg} backdrop-blur-sm rounded-2xl ${colors.cardBorder} shadow-lg mb-6 overflow-hidden`}>
      {/* Header */}
      <div
        className={`flex items-center justify-between p-6 cursor-pointer ${colors.tableRowHover} transition-colors`}
        onClick={onToggle}
      >
        <h2 className={`text-lg font-bold ${colors.textPrimary} flex items-center`}>
          <FileText className={`w-5 h-5 mr-2 ${colors.textSecondary}`} />
          {t('sessionDetails.notes.title')}
        </h2>
        <div className="flex items-center space-x-3">
          {notes.length > 0 && (
            <span className={`px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-semibold`}>
              {t('sessionDetails.notes.count', { count: notes.length })}
            </span>
          )}
          {expanded ? (
            <ChevronUp className={`w-5 h-5 ${colors.textTertiary}`} />
          ) : (
            <ChevronDown className={`w-5 h-5 ${colors.textTertiary}`} />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className={`border-t ${colors.tableBorder}`}>
          <div className="p-6">
            {/* Success Message */}
            {saveSuccess && (
              <div className="mb-4 flex items-center space-x-2 p-3 bg-green-500/10 border border-green-500/30 rounded-xl">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm text-green-400">{t('sessionDetails.notes.saveSuccess')}</span>
              </div>
            )}

            {/* Add New Note Button */}
            {!isAddingNote && (
              <button
                onClick={() => setIsAddingNote(true)}
                className="mb-4 flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-sm hover:shadow-md transform hover:scale-[1.02]"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm">{t('sessionDetails.notes.addNote')}</span>
              </button>
            )}

            {/* Add Note Form */}
            {isAddingNote && (
              <div className={`mb-4 p-4 ${colors.inputBg} border ${colors.inputBorder} rounded-xl`}>
                <textarea
                  value={newNoteContent}
                  onChange={(e) => setNewNoteContent(e.target.value)}
                  placeholder={t('sessionDetails.notes.placeholder')}
                  rows={4}
                  autoFocus
                  className={`w-full px-4 py-3 ${colors.inputBg} border ${colors.inputBorder} rounded-xl focus:outline-none focus:ring-2 ${colors.inputFocus} text-sm ${colors.inputText} ${colors.inputPlaceholder} resize-none`}
                />
                <div className="mt-3 flex items-center justify-end space-x-2">
                  <button
                    onClick={() => {
                      setIsAddingNote(false);
                      setNewNoteContent('');
                    }}
                    className={`px-4 py-2 ${colors.bgHover} rounded-lg transition-colors text-sm ${colors.textSecondary}`}
                  >
                    {t('sessionDetails.notes.cancel')}
                  </button>
                  <button
                    onClick={handleAddNote}
                    disabled={saving || !newNoteContent.trim()}
                    className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 font-semibold shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">{t('sessionDetails.notes.saving')}</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span className="text-sm">{t('sessionDetails.notes.save')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Notes List */}
            {notes.length === 0 && !isAddingNote ? (
              <div className={`text-center py-8 ${colors.textMuted}`}>
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">{t('sessionDetails.notes.empty')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className={`p-4 ${colors.inputBg} border ${colors.inputBorder} rounded-xl`}
                  >
                    {editingNoteId === note.id ? (
                      // Edit Mode
                      <>
                        <textarea
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                          rows={4}
                          autoFocus
                          className={`w-full px-4 py-3 ${colors.inputBg} border ${colors.inputBorder} rounded-xl focus:outline-none focus:ring-2 ${colors.inputFocus} text-sm ${colors.inputText} resize-none`}
                        />
                        <div className="mt-3 flex items-center justify-end space-x-2">
                          <button
                            onClick={handleCancelEdit}
                            className={`px-3 py-1.5 ${colors.bgHover} rounded-lg transition-colors text-sm ${colors.textSecondary}`}
                          >
                            {t('sessionDetails.notes.cancel')}
                          </button>
                          <button
                            onClick={() => handleSaveEdit(note.id)}
                            disabled={saving || !editingContent.trim()}
                            className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                          >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            <span>{t('sessionDetails.notes.save')}</span>
                          </button>
                        </div>
                      </>
                    ) : (
                      // Display Mode
                      <>
                        <p className={`${colors.textPrimary} text-sm whitespace-pre-wrap`}>{note.content}</p>

                        {/* Note Meta Info */}
                        <div className={`mt-4 pt-3 border-t ${colors.tableBorder} flex items-center justify-between`}>
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <User className={`w-4 h-4 ${colors.textMuted}`} />
                              <span className={`text-xs ${colors.textMuted}`}>{note.authorName || t('sessionDetails.notes.unknown')}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Clock className={`w-4 h-4 ${colors.textMuted}`} />
                              <span className={`text-xs ${colors.textMuted}`}>
                                {moment(note.createdAt).format('MMM D, YYYY h:mm A')}
                                {note.updatedAt !== note.createdAt && ` ${t('sessionDetails.notes.edited')}`}
                              </span>
                            </div>
                          </div>

                          {/* Edit/Delete buttons - only for note owner */}
                          {canEditNote(note) && (
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleStartEdit(note)}
                                className={`flex items-center space-x-1 px-2 py-1 ${colors.bgHover} rounded-lg transition-colors text-xs ${colors.textSecondary}`}
                              >
                                <Edit3 className="w-3 h-3" />
                                <span>{t('sessionDetails.notes.edit')}</span>
                              </button>
                              <button
                                onClick={() => handleDeleteNote(note.id)}
                                className="flex items-center space-x-1 px-2 py-1 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors text-xs text-red-400"
                              >
                                <Trash2 className="w-3 h-3" />
                                <span>{t('sessionDetails.notes.delete')}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
